import { LegacyCustomerOrderBase } from '../contracts/customer-order.js';
import { Currency } from '../models/serializable/currency.js';
import { EventTicketOrder } from '../models/serializable/event-ticket-order.js';
import { OnlineCalendarOrder } from '../models/serializable/online-calendar-order.js';
import { OnlineEventPassOrder } from '../models/serializable/online-event-pass-order.js';
import { OnlineEventTicketOrder } from '../models/serializable/online-event-ticket-order.js';
import { OnlineProductOrder } from '../models/serializable/online-product-order.js';
import { Price } from '../models/serializable/price.js';
import { IOrderPriceBreakdown } from './core/order-price-breakdown.js';
import { OnlineCalendarOrderPaymentHelper, PhoneCalendarOrderPaymentHelper } from './core/calendar-order-payment-helper.js';
import { EventPassOrderPaymentHelper } from './core/event-pass-order-payment-helper.js';
import { OfflineTicketOrderPaymentHelper } from './core/offline-ticket-order-payment-helper.js';
import { OnlineTicketOrderPaymentHelper } from './core/online-ticket-order-payment-helper.js';
import { ProductOrderPaymentHelper } from './core/product-order-payment-helper.js';
import { IPaymentHelperAdapter } from './core/payment-helper-adapter.js';
import { IPaymentHelperConfig, NOOP_PAYMENT_ADAPTER_CONFIG } from './core/payment-helper-config.js';
import { LegacyOrderDiscountBreakdown } from './core/discount-breakdown.js';
import { LegacyOrderFeeBreakdown } from './core/fee-breakdown.js';
import { EventTicket } from '../models/serializable/event-ticket.js';
import { DiscountHelper } from './discount-helper.js';
import { TicketSupplement } from '../models/serializable/ticket-supplement.js';
import { PhoneCalendarOrder } from '../models/serializable/phone-calendar-order.js';

export class LegacyPaymentHelper {
  private constructor() {
    this._impl = new Map<string, IPaymentHelperAdapter>();

    this._impl.set(OnlineCalendarOrder.TYPE_ID, new OnlineCalendarOrderPaymentHelper());
    this._impl.set(PhoneCalendarOrder.TYPE_ID, new PhoneCalendarOrderPaymentHelper());
    this._impl.set(OnlineEventPassOrder.TYPE_ID, new EventPassOrderPaymentHelper());
    this._impl.set(EventTicketOrder.TYPE_ID, new OfflineTicketOrderPaymentHelper());
    this._impl.set(OnlineEventTicketOrder.TYPE_ID, new OnlineTicketOrderPaymentHelper());
    this._impl.set(OnlineProductOrder.TYPE_ID, new ProductOrderPaymentHelper());
  }

  private readonly _impl: Map<string, IPaymentHelperAdapter>;

  private static _instance: LegacyPaymentHelper = new LegacyPaymentHelper();

  static getAdapterForOrder(order: LegacyCustomerOrderBase): IPaymentHelperAdapter | null {
    return this._instance._impl.get(order.$typeId) ?? null;
  }

  static getCurrencyOfOrder(order: LegacyCustomerOrderBase): Currency | null {
    return this.getAdapterForOrder(order)?.getCurrencyOfOrder(order) ?? null;
  }

  static calculateDiscountForOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    return this.getAdapterForOrder(order)?.calculateDiscountForOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateCheckoutPriceBreakdownForOnlineOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): IOrderPriceBreakdown | null {
    return this.getAdapterForOrder(order)?.calculateCheckoutPriceBreakdownForOnlineOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateDiscountBreakdownForOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): LegacyOrderDiscountBreakdown | null {
    return this.getAdapterForOrder(order)?.calculateDiscountBreakdownForOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateFeeBreakdownForOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): LegacyOrderFeeBreakdown | null {
    return this.getAdapterForOrder(order)?.calculateFeeBreakdownForOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateCheckoutPriceForOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    return this.getAdapterForOrder(order)?.calculateCheckoutPriceForOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateCheckoutPriceForDiscountProvider(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    return this.getAdapterForOrder(order)?.calculateCheckoutPriceForDiscountProvider(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateIncomeForOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    return this.getAdapterForOrder(order)?.calculateIncomeForOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateIncomeWithoutDiscountForOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    return this.getAdapterForOrder(order)?.calculateIncomeWithoutDiscountForOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static canCreateRefundForOnlineOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): boolean | null {
    return this.getAdapterForOrder(order)?.canCreateRefundForOnlineOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateExistingRefundForOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    return this.getAdapterForOrder(order)?.calculateExistingRefundForOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateRemainingRefundForOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    return this.getAdapterForOrder(order)?.calculateRemainingRefundForOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateMaxRefundForOnlineOrder(order: LegacyCustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    return this.getAdapterForOrder(order)?.calculateMaxRefundForOnlineOrder(order, config ?? NOOP_PAYMENT_ADAPTER_CONFIG) ?? null;
  }

  static calculateCheckoutPriceForTicketSupplement(supplement: TicketSupplement): Price {
    return supplement.template.price.clone();
  }

  static calculateCheckoutPriceForEventTicket(ticket: EventTicket, { applySupplement }: { applySupplement: boolean }): Price {
    return this.calculatePriceForEventTicket({ ticket, applyDiscount: true, applySupplement });
  }

  static calculateIncomeForEventTicket(ticket: EventTicket, { applySupplement }: { applySupplement: boolean }): Price {
    return this.calculatePriceForEventTicket({ ticket, applyDiscount: true, applySupplement });
  }

  static calculateIncomeWithoutDiscountForEventTicket(ticket: EventTicket, { applySupplement }: { applySupplement: boolean }): Price {
    return this.calculatePriceForEventTicket({ ticket, applyDiscount: false, applySupplement });
  }

  static calculateOriginalIncomeForOfflineTicketOrder(order: EventTicketOrder, { applyProducts, applySupplements }: {
      applyProducts: boolean,
      applySupplements: boolean,
  }): Price | null {
    if(order.tickets.length <= 0) {
      return null;
    }

    if(order.tickets[0].template.originalPrice == null) {
      return null;
    }

    let currency: Currency | null = null;
    if(order.tickets.length > 0) {
      currency = order.tickets[0].template.originalPrice.currency;
    }

    if(currency == null) {
      return null;
    }

    let orderPrice = Price.zero(currency);

    for(const ticket of order.tickets) {
      if(ticket.template.originalPrice == null) {
        return null;
      }
      if(ticket.template.originalPrice.currency !== currency) {
        return null;
      }

      orderPrice = orderPrice.add(ticket.template.originalPrice);
    }

    if(applyProducts) {
      for(const item of order.products) {
        if(item.product.price.currency != currency) {
          return null;
        }

        orderPrice = orderPrice.add(item.product.price.scale(item.quantity));
      }
    }

    if(applySupplements) {
      for(const ticket of order.tickets) {
        if(ticket.supplement == null) {
          continue;
        }
        if(ticket.supplement.template.price.currency != currency) {
          return null;
        }

        const supplementPrice = ticket.supplement.template.price;
        orderPrice = orderPrice.add(supplementPrice);
      }
    }

    return orderPrice;
  }

  private static calculatePriceForEventTicket({ ticket, applySupplement, applyDiscount }: {
    ticket: EventTicket,
    applySupplement: boolean,
    applyDiscount: boolean,
  }): Price {
    let ticketPrice = DiscountHelper.calculateDiscountedPriceForSingleItem(ticket.template.price, applyDiscount ? ticket.discount : null);
    if(applySupplement && ticket.supplement != null) {
      ticketPrice = ticketPrice.add(ticket.supplement.template.price);
    }

    return ticketPrice;
  }
}
